import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Content } from '@wajeez/wajeez.microservices';
import _ from 'lodash';

import {
  getLibraryCount,
  getMyStartedContent,
  getMyCompletedContent,
  getMyFollowedContent,
  getMyBookmarkedContainer,
  getMyHighlightedContainer,
} from './thunks';
import { MyLibraryState } from './types';

export const name = 'myLibrary';

const initialState: MyLibraryState = {
  libraryCount: {},
  myStartedContent: {},
  myCompletedContent: {},
  myFollowedContent: {},
  myBookmarkedContainer: {
    data: [],
    meta: { hasNextPage: false, totalCount: 0, currentPage: 1 },
  },
  myHighlightedContainer: {
    data: [],
    meta: { hasNextPage: false, totalCount: 0, currentPage: 1 },
  },

  libraryCountStatus: 'idle',
  myStartedContentStatus: 'idle',
  myCompletedContentStatus: 'idle',
  myFollowedContentStatus: 'idle',
  myBookmarkedContainerStatus: 'idle',
  myHighlightedContainerStatus: 'idle',
};

export const getLibraryCountAsync = createAsyncThunk(
  name + '/getLibraryCount',
  getLibraryCount
);

export const getMyStartedContentAsync = createAsyncThunk(
  name + '/getMyStartedContent',
  getMyStartedContent
);

export const getMyCompletedContentAsync = createAsyncThunk(
  name + '/getMyCompletedContent',
  getMyCompletedContent
);

export const getMyFollowedContentAsync = createAsyncThunk(
  name + '/getMyFollowedContent',
  getMyFollowedContent
);

export const getMyBookmarkedContainerAsync = createAsyncThunk(
  name + '/getMyBookmarkedContainer',
  getMyBookmarkedContainer
);

export const getMyHighlightedContainerAsync = createAsyncThunk(
  name + '/getMyHighlightedContainer',
  getMyHighlightedContainer
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: (_) => {
      return initialState;
    },
    clearMyLibrary: (state) => {
      return {
        ...state,
        myStartedContent: initialState.myStartedContent,
        myCompletedContent: initialState.myCompletedContent,
        myFollowedContent: initialState.myFollowedContent,
        myBookmarkedContainer: initialState.myBookmarkedContainer,
        myHighlightedContainer: initialState.myHighlightedContainer,
      };
    },

    clearMyStartedContent: (state) => {
      state.myStartedContent = {};
    },
    clearMyCompletedContent: (state) => {
      state.myCompletedContent = {};
    },
    clearMyFollowedContent: (state) => {
      state.myFollowedContent = {};
    },
    clearMyBookmarkedContainer: (state) => {
      state.myBookmarkedContainer = initialState.myBookmarkedContainer;
    },
    clearMyHighlightedContainer: (state) => {
      state.myHighlightedContainer = initialState.myHighlightedContainer;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getLibraryCountAsync.pending, (state) => {
        state.libraryCountStatus = 'loading';
      })
      .addCase(getLibraryCountAsync.fulfilled, (state, action) => {
        state.libraryCountStatus = 'idle';
        state.libraryCount = action.payload;
      })
      .addCase(getLibraryCountAsync.rejected, (state) => {
        state.libraryCountStatus = 'failed';
      })
      .addCase(getMyStartedContentAsync.pending, (state) => {
        state.myStartedContentStatus = 'loading';
      })
      .addCase(getMyStartedContentAsync.fulfilled, (state, action) => {
        const uniqueId = Object.keys(action.payload)[0];

        if (state.myStartedContent[uniqueId]) {
          const stateObj = _.reduce(
            state.myStartedContent[uniqueId].data,
            function (
              obj: { [key: string]: Content.LibraryItemOutput },
              param
            ) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const dataObj = _.reduce(
            action.payload[uniqueId].data,
            function (
              obj: { [key: string]: Content.LibraryItemOutput },
              param
            ) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const totalObj = {
            ...stateObj,
            ...dataObj,
          };

          return {
            ...state,
            myStartedContentStatus: 'idle',
            myStartedContent: {
              ...state.myStartedContent,
              [uniqueId]: {
                data: Object.values(totalObj),
                meta: action.payload[uniqueId].meta,
              },
            },
          };
        } else {
          return {
            ...state,
            myStartedContentStatus: 'idle',
            myStartedContent: {
              ...state.myStartedContent,
              ...action.payload,
            },
          };
        }
      })
      .addCase(getMyStartedContentAsync.rejected, (state) => {
        state.myStartedContentStatus = 'failed';
      })
      .addCase(getMyCompletedContentAsync.pending, (state) => {
        state.myCompletedContentStatus = 'loading';
      })
      .addCase(getMyCompletedContentAsync.fulfilled, (state, action) => {
        const uniqueId = Object.keys(action.payload)[0];

        if (state.myCompletedContent[uniqueId]) {
          const stateObj = _.reduce(
            state.myCompletedContent[uniqueId].data,
            function (
              obj: { [key: string]: Content.LibraryItemOutput },
              param
            ) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const dataObj = _.reduce(
            action.payload[uniqueId].data,
            function (
              obj: { [key: string]: Content.LibraryItemOutput },
              param
            ) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const totalObj = {
            ...stateObj,
            ...dataObj,
          };

          return {
            ...state,
            myCompletedContentStatus: 'idle',
            myCompletedContent: {
              ...state.myCompletedContent,
              [uniqueId]: {
                data: Object.values(totalObj),
                meta: action.payload[uniqueId].meta,
              },
            },
          };
        } else {
          return {
            ...state,
            myCompletedContentStatus: 'idle',
            myCompletedContent: {
              ...state.myCompletedContent,
              ...action.payload,
            },
          };
        }
      })
      .addCase(getMyCompletedContentAsync.rejected, (state) => {
        state.myCompletedContentStatus = 'failed';
      })
      .addCase(getMyFollowedContentAsync.pending, (state) => {
        state.myFollowedContentStatus = 'loading';
      })
      .addCase(getMyFollowedContentAsync.fulfilled, (state, action) => {
        const uniqueId = Object.keys(action.payload)[0];

        if (state.myFollowedContent[uniqueId]) {
          const stateObj = _.reduce(
            state.myFollowedContent[uniqueId].data,
            function (
              obj: { [key: string]: Content.LibraryItemOutput },
              param
            ) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const dataObj = _.reduce(
            action.payload[uniqueId].data,
            function (
              obj: { [key: string]: Content.LibraryItemOutput },
              param
            ) {
              if (param.id) obj[param.id] = param;

              return obj;
            },
            {}
          );

          const totalObj = {
            ...stateObj,
            ...dataObj,
          };

          return {
            ...state,
            myFollowedContentStatus: 'idle',
            myFollowedContent: {
              ...state.myFollowedContent,
              [uniqueId]: {
                data: Object.values(totalObj),
                meta: action.payload[uniqueId].meta,
              },
            },
          };
        } else {
          return {
            ...state,
            myFollowedContentStatus: 'idle',
            myFollowedContent: {
              ...state.myFollowedContent,
              ...action.payload,
            },
          };
        }
      })
      .addCase(getMyFollowedContentAsync.rejected, (state) => {
        state.myFollowedContentStatus = 'failed';
      })
      .addCase(getMyBookmarkedContainerAsync.pending, (state) => {
        state.myBookmarkedContainerStatus = 'loading';
      })
      .addCase(getMyBookmarkedContainerAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.myBookmarkedContainer.data,
          function (obj: { [key: string]: Content.LibraryItemOutput }, param) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (obj: { [key: string]: Content.LibraryItemOutput }, param) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        return {
          ...state,
          myBookmarkedContainerStatus: 'idle',
          myBookmarkedContainer: {
            data: Object.values(totalObj),
            meta: action.payload.meta,
          },
        };
      })
      .addCase(getMyBookmarkedContainerAsync.rejected, (state) => {
        state.myBookmarkedContainerStatus = 'failed';
      })
      .addCase(getMyHighlightedContainerAsync.pending, (state) => {
        state.myHighlightedContainerStatus = 'loading';
      })
      .addCase(getMyHighlightedContainerAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.myHighlightedContainer.data,
          function (obj: { [key: string]: Content.LibraryItemOutput }, param) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (obj: { [key: string]: Content.LibraryItemOutput }, param) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        return {
          ...state,
          myHighlightedContainerStatus: 'idle',
          myHighlightedContainer: {
            data: Object.values(totalObj),
            meta: action.payload.meta,
          },
        };
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
